/**
 * Shared logic for room availability calculation.
 * Extracted from app/room-availability/page.tsx for use in the specialized StudySpaceRow.
 */

export const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAY_ABBREV: Record<string, string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

// Operating hours: 7:00–22:00 (minutes from midnight)
export const DAY_START_MIN = 7 * 60;
export const DAY_END_MIN = 22 * 60;

export interface RoomInfo {
  roomKey: string;
  building_code: string;
  building_num: string;
  room_number: string;
  campus_abbrev: string;
  campus_name: string;
  label: string;
}

export interface UsageBlock {
  day: string;
  startMinute: number;
  endMinute: number;
}

export interface RoomAvailabilityData {
  metadata?: {
    semester: string;
    semesterLabel: string;
    lastRefreshAt: string | null;
    lastRefreshBasis: string;
    generatedAt: string;
    sourcePath: string;
    coverage: string;
  };
  rooms: RoomInfo[];
  usage: Record<string, UsageBlock[]>;
  dayOrder: string[];
}

export function formatMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const period = h >= 12 ? 'pm' : 'am';
  const displayH = h % 12 || 12;
  return `${displayH}:${m.toString().padStart(2, '0')}${period}`;
}

export function durationMinutes(start: number, end: number): number {
  return end - start;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Compute available intervals for one day: [DAY_START, DAY_END] minus busy blocks. */
export function availableIntervals(
  busy: { startMinute: number; endMinute: number }[]
): { start: number; end: number }[] {
  const sorted = [...busy].sort((a, b) => a.startMinute - b.startMinute);
  const out: { start: number; end: number }[] = [];
  let cur = DAY_START_MIN;
  for (const b of sorted) {
    const start = Math.max(b.startMinute, DAY_START_MIN);
    const end = Math.min(b.endMinute, DAY_END_MIN);
    if (start > cur && end > cur) {
      out.push({ start: cur, end: Math.min(start, DAY_END_MIN) });
    }
    cur = Math.max(cur, end);
  }
  if (cur < DAY_END_MIN) {
    out.push({ start: cur, end: DAY_END_MIN });
  }
  return out;
}

/** True if room has no usage overlapping [startMinute, endMinute) on the given day. */
export function isRoomFreeForBlock(
  usage: Record<string, UsageBlock[]>,
  roomKey: string,
  day: string,
  startMinute: number,
  endMinute: number
): boolean {
  if (startMinute < DAY_START_MIN || endMinute > DAY_END_MIN) return false;
  const blocks = (usage[roomKey] ?? []).filter((b) => b.day === day);
  for (const b of blocks) {
    if (b.startMinute < endMinute && b.endMinute > startMinute) return false;
  }
  return true;
}

/** Returns the current day name (Monday, Tuesday, etc.) */
export function getCurrentDayName(): string {
  const d = new Date().getDay();
  return DAY_ORDER[d === 0 ? 6 : d - 1];
}

/** Returns current time in minutes from midnight */
export function getCurrentMinutes(): number {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}
